import React, { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import { AdCreativeEvent, AdCreativeWithUrls } from 'common/api/v1/types'
import { Api, AppDispatch, useRoutes } from '../../../store'
import { useDispatch } from 'react-redux'
import { createAdCreativeEvent } from '../../../redux/actions/adCreativeActions'
import { AdCreativeThumbnail } from '../../common/Thumbnail'
import ErrorOutline from '@mui/icons-material/ErrorOutline'
import { AudioMetadata, VideoMetadata } from './Edit/Metadata'
import { formatBitrate } from 'common/api/v1/helpers'
import DialogActions from '@mui/material/DialogActions'
import { ButtonsPane } from '../../common/Form'
import { useNavigate } from 'react-router'

type Props = {
  adCreative?: AdCreativeWithUrls
  onClose: () => void
}

const REQUIRED_EVENTS: AdCreativeEvent[] = [
  AdCreativeEvent.uploadOk,
  AdCreativeEvent.thumbnailOk,
  AdCreativeEvent.probeOk,
]

const refreshAdCreative = async (id: string): Promise<AdCreativeWithUrls> => {
  return Api.adCreativesApi.getAdCreative(id)
}

const UploadDialog = ({ adCreative: initialAdCreative, onClose }: Props) => {
  const [adCreative, setAdCreative] = useState<AdCreativeWithUrls | undefined>(initialAdCreative)
  const [uploading, setUploading] = useState(false)
  const [uploadStarted, setUploadStarted] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const pollingRef = useRef<NodeJS.Timeout | null>(null)
  const POLLING_INTERVAL_MS = 100
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const routes = useRoutes()

  useEffect(() => {
    setAdCreative(initialAdCreative)
    // If this creative started its upload process, start polling to check its status
    if (initialAdCreative?.events.includes(AdCreativeEvent.uploadOk)) {
      startPolling(initialAdCreative.id)
      setUploadStarted(true)
    }
  }, [initialAdCreative])

  const startPolling = (id: string) => {
    if (pollingRef.current) return
    pollingRef.current = setInterval(async () => {
      try {
        const updated = await refreshAdCreative(id)
        if (pollingRef.current) {
          setAdCreative(updated)
          const allOk = REQUIRED_EVENTS.every((event) => updated.events.includes(event))
          const anyFail = updated.events.some((e) =>
            [AdCreativeEvent.thumbnailFailed, AdCreativeEvent.probeFailed].includes(e),
          )

          if (allOk || anyFail) {
            stopPolling()
          }
        }
      } catch (err) {
        setUploadError('Failed to refresh ad creative')
        stopPolling()
      }
    }, POLLING_INTERVAL_MS)
  }

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
    setUploadStarted(false)
  }

  useEffect(() => {
    return stopPolling // cleanup on unmount
  }, [])

  if (!adCreative) return null

  const handleFileDrop = async (e: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>) => {
    const file = 'files' in e.target ? e.target.files?.[0] : 'dataTransfer' in e ? e.dataTransfer.files?.[0] : undefined
    if (!file) return

    setUploading(true)
    setUploadError(null)
    setUploadStarted(true)

    try {
      await fetch(adCreative.uploadUrl, {
        method: 'PUT',
        body: file,
      })
      startPolling(adCreative.id)
      dispatch(createAdCreativeEvent({ id: adCreative.id, type: AdCreativeEvent.uploadOk }))
    } catch (err) {
      dispatch(createAdCreativeEvent({ id: adCreative.id, type: AdCreativeEvent.uploadFailed }))
      setAdCreative({ ...adCreative, events: [...adCreative.events, AdCreativeEvent.uploadFailed] })
      setUploadError('Upload failed')
      setUploadStarted(false)
    } finally {
      setUploading(false)
    }
  }

  const renderStatusIcon = (status?: 'pending' | 'ok' | 'fail', showSpinner = false) => {
    if (status === 'ok') return <CheckCircleIcon color="success" />
    if (status === 'fail') return <ErrorIcon color="error" />
    if (showSpinner) return <CircularProgress size={20} />
    return <RadioButtonUncheckedIcon color="disabled" />
  }

  const getEventStatus = (eventOk: AdCreativeEvent, eventFail: AdCreativeEvent) => {
    if (adCreative.events.includes(eventOk)) return 'ok'
    if (adCreative.events.includes(eventFail)) return 'fail'
    return 'pending'
  }

  const hasUploaded = adCreative.events.includes(AdCreativeEvent.uploadOk)
  const { metadata } = adCreative

  const formatDuration = (ms: number | undefined): string =>
    typeof ms === 'number' ? `${(ms / 1000).toFixed(3)} s` : '—'

  const handleClose = () => {
    setAdCreative(undefined)
    stopPolling()
    onClose()
  }

  return (
    <Dialog data-test-id={'ad-creative-upload-dialog'} open onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h2" component="div" sx={{ fontWeight: 'normal' }}>
          Upload media file for{' '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            {adCreative.name}
          </Box>
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" gap={2} mt={2} alignItems="center">
          {/* Upload box */}
          <Box
            flex={1}
            sx={{
              border: '2px dashed grey',
              borderRadius: 2,
              p: 2,
              textAlign: 'center',
              cursor: hasUploaded ? 'default' : 'pointer',
              position: 'relative',
              opacity: hasUploaded ? 0.5 : 1,
            }}
            onClick={() => {
              if (!hasUploaded) document.getElementById('fileInput')?.click()
            }}
            onDragOver={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onDrop={(e) => {
              e.preventDefault()
              e.stopPropagation()
              if (hasUploaded) return
              void handleFileDrop(e)
            }}
          >
            <input id="fileInput" type="file" hidden onChange={handleFileDrop} disabled={hasUploaded} />
            {uploading ? (
              <CircularProgress />
            ) : (
              <>
                <CloudUploadIcon fontSize="large" />
                <Typography mt={1}>Click or drop file to upload</Typography>
                {uploadError && <Typography color="error">{uploadError}</Typography>}
              </>
            )}
          </Box>

          {/* Event status list */}
          <Stack spacing={1} width={200}>
            <Box display="flex" alignItems="center" gap={1}>
              {renderStatusIcon(getEventStatus(AdCreativeEvent.uploadOk, AdCreativeEvent.uploadFailed), uploadStarted)}
              <Typography>Upload</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {renderStatusIcon(getEventStatus(AdCreativeEvent.probeOk, AdCreativeEvent.probeFailed), uploadStarted)}
              <Typography>Probe</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {renderStatusIcon(
                getEventStatus(AdCreativeEvent.thumbnailOk, AdCreativeEvent.thumbnailFailed),
                uploadStarted,
              )}
              <Typography>Thumbnail</Typography>
            </Box>
          </Stack>
        </Box>

        {/* Collapse for thumbnail and metadata */}
        <Collapse in={!!metadata} timeout="auto" unmountOnExit>
          <Box mt={3} p={2} borderTop="1px solid #ccc">
            {adCreative.errorMessage && (
              <Box
                mb={2}
                px={1.5}
                py={1}
                bgcolor="error.main"
                color="error.contrastText"
                borderRadius={1}
                display="flex"
                alignItems="center"
                gap={1}
                maxWidth="fit-content"
              >
                <ErrorOutline fontSize="small" />
                <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                  {adCreative.errorMessage}
                </Typography>
              </Box>
            )}
            <Box display="flex" gap={3}>
              {/* Metadata Section */}
              <Stack spacing={1} flex={1}>
                <Typography>
                  <strong>Duration:</strong>{' '}
                  <Box component="span" color="text.secondary">
                    {formatDuration(metadata?.format?.durationMs)}
                  </Box>
                </Typography>
                <Typography>
                  <strong>Bitrate:</strong>{' '}
                  <Box component="span" color="text.secondary">
                    {formatBitrate(metadata?.format?.bitrate)}
                  </Box>
                </Typography>

                <VideoMetadata metadata={metadata} sx={{ mt: 2, p: 2 }} />
                <AudioMetadata metadata={metadata} sx={{ mt: 2, p: 2 }} />
              </Stack>

              {/* Thumbnail Section */}
              <Box width="50%" overflow="hidden" sx={{ mt: 'auto', mb: 'auto' }}>
                <AdCreativeThumbnail adCreative={adCreative} />
              </Box>
            </Box>
          </Box>
        </Collapse>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 2 }}>
        {!hasUploaded ? (
          <ButtonsPane
            main={{
              Cancel: {
                onClick: onClose,
              },
            }}
          />
        ) : (
          <ButtonsPane
            main={{
              Dismiss: {
                onClick: handleClose,
              },
              Edit: {
                onClick: () => navigate(routes.adCreativesUpdate({ id: adCreative.id })),
                primary: true,
              },
            }}
          />
        )}
      </DialogActions>
    </Dialog>
  )
}

export default UploadDialog
